import { FileMetadata } from "../components/input-files/definitions";
import { getFileNameWithoutExtension } from "./date-format";

export const generateId = (): string => Math.random().toString(36).substr(2, 9);

export const truncateFileName = (fileName: string, maxLength = 35): string => {
  if (fileName.length <= maxLength) return fileName;

  const start = fileName.substring(0, Math.floor(maxLength / 2) - 2);
  const end = fileName.substring(
    fileName.length - Math.floor(maxLength / 2) + 2,
  );
  return `${start}...${end}`;
};

export const MAX_FILES_PER_GROUP = 5;

export const createFileMetadata = (
  files: File[],
  defaultDocumentType?: string,
  startGroup = 1,
): FileMetadata[] => {
  return files.map((file, index) => ({
    file,
    fileName: getFileNameWithoutExtension(file.name),
    documentType: defaultDocumentType || "",
    isRenaming: false,
    id: generateId(),
    group: startGroup + index,
  }));
};

export const removeFileFromMetadata = (
  fileMetadata: FileMetadata[],
  id: string,
): { updatedMetadata: FileMetadata[]; updatedFiles: File[] } => {
  const updatedMetadata = fileMetadata.filter((item) => item.id !== id);
  const updatedFiles = updatedMetadata.map((item) => item.file);

  return { updatedMetadata, updatedFiles };
};

export const updateFileRenamingState = (
  fileMetadata: FileMetadata[],
  id: string,
  isRenaming: boolean,
): FileMetadata[] => {
  return fileMetadata.map((item) =>
    item.id === id ? { ...item, isRenaming } : item,
  );
};

export const updateFileNameInMetadata = (
  fileMetadata: FileMetadata[],
  id: string,
  newName: string,
): FileMetadata[] => {
  return fileMetadata.map((item) =>
    item.id === id ? { ...item, fileName: newName } : item,
  );
};

export const updateDocumentTypeInMetadata = (
  fileMetadata: FileMetadata[],
  id: string,
  documentType: string,
): FileMetadata[] => {
  return fileMetadata.map((item) =>
    item.id === id ? { ...item, documentType } : item,
  );
};

export const updateGroupInMetadata = (
  fileMetadata: FileMetadata[],
  id: string,
  group: number,
): FileMetadata[] => {
  return fileMetadata.map((item) =>
    item.id === id ? { ...item, group } : item,
  );
};

export const filterUniqueFiles = (
  existingFiles: File[],
  newFiles: File[],
): File[] => {
  const existingFileNames = existingFiles.map((file) => file.name);
  return newFiles.filter((file) => !existingFileNames.includes(file.name));
};

export const combineFileMetadata = (
  existingMetadata: FileMetadata[],
  newFiles: File[],
  defaultDocumentType?: string,
): FileMetadata[] => {
  const nextGroup =
    existingMetadata.reduce((max, item) => Math.max(max, item.group), 0) + 1;
  const newMetadata = createFileMetadata(
    newFiles,
    defaultDocumentType,
    nextGroup,
  );
  return [...existingMetadata, ...newMetadata];
};

export const hasEmptyFileNames = (fileMetadata: FileMetadata[]): boolean => {
  return fileMetadata.some((item) => !item.fileName.trim());
};

export const hasEmptyDocumentTypes = (
  fileMetadata: FileMetadata[],
): boolean => {
  return fileMetadata.some((item) => !item.documentType);
};

export const getOversizedGroup = (
  fileMetadata: FileMetadata[],
  maxFilesPerGroup: number,
): number | undefined => {
  const counts = new Map<number, number>();
  for (const item of fileMetadata) {
    counts.set(item.group, (counts.get(item.group) ?? 0) + 1);
  }
  for (const [group, count] of counts) {
    if (count > maxFilesPerGroup) {
      return group;
    }
  }
  return undefined;
};

export const validateFileMetadata = (
  fileMetadata: FileMetadata[],
  requireDocumentTypes = false,
  maxFilesPerGroup?: number,
): { isValid: boolean; errorMessage?: string } => {
  if (fileMetadata.length === 0) {
    return { isValid: false, errorMessage: "Please select files" };
  }

  if (hasEmptyFileNames(fileMetadata)) {
    return {
      isValid: false,
      errorMessage: requireDocumentTypes
        ? "Please ensure all files have names and types"
        : "Please ensure all files have names",
    };
  }

  if (requireDocumentTypes && hasEmptyDocumentTypes(fileMetadata)) {
    return {
      isValid: false,
      errorMessage: "Please ensure all files have names and types",
    };
  }

  if (maxFilesPerGroup !== undefined) {
    const oversizedGroup = getOversizedGroup(fileMetadata, maxFilesPerGroup);
    if (oversizedGroup !== undefined) {
      return {
        isValid: false,
        errorMessage: `A group can have at most ${maxFilesPerGroup} files`,
      };
    }
  }

  return { isValid: true };
};
